import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import '../styles/Home.css';

const Home = () => {
  const { i18n } = useTranslation();
  const [services, setServices] = useState([]);
  const [homeContent, setHomeContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const currentLang = i18n.language || 'pt';

  // Fetch services and home content from API
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch services
      const servicesResponse = await axios.get('/api/services');
      if (servicesResponse.data.success) {
        const topServices = servicesResponse.data.allServices
          .filter(s => s.isActive)
          .sort((a, b) => a.order - b.order)
          .slice(0, 3);
        setServices(topServices);
      }

      // Fetch home content
      const homeResponse = await axios.get('/api/home-content');
      if (homeResponse.data.success) {
        setHomeContent(homeResponse.data.content);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  // Initialize scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.fade-in, .slide-in-left, .slide-in-right');
    animatedElements.forEach(el => observer.observe(el));

    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      const hero = document.querySelector('.hero');
      if (hero) {
        hero.style.transform = `translateY(${scrolled * 0.5}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, [services, homeContent]);

  if (loading || !homeContent) {
    return (
      <div className="home">
        <div className="loading-home">
          <p>{currentLang === 'pt' ? 'Carregando...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home">
      {/* Hero Section with Parallax - DYNAMIC */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1>{homeContent.hero.title[currentLang]}</h1>
            <p>{homeContent.hero.subtitle[currentLang]}</p>
            <div className="hero-buttons">
              <Link to="/booking" className="btn btn-primary">
                {homeContent.hero.ctaText[currentLang]}
              </Link>
              <Link to="/services" className="btn btn-secondary">
                {currentLang === 'pt' ? 'Saber Mais' : 'Learn More'}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - DYNAMIC */}
      <section className="section features fade-in">
        <div className="container">
          <h2 className="text-center">
            {homeContent.sectionTitles?.features?.[currentLang] || (currentLang === 'pt' ? 'Como Posso Ajudar' : 'How I Can Help')}
          </h2>
          <div className="features-grid">
            {homeContent.features
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((feature, index) => (
                <div 
                  key={index} 
                  className="feature-card fade-in"
                  style={{ transitionDelay: `${index * 0.1}s` }}
                >
                  <div className="feature-icon">{feature.icon}</div>
                  <h3>{feature.title[currentLang]}</h3>
                  <p>{feature.description[currentLang]}</p>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* Services Preview - DYNAMIC */}
      <section className="section services-preview fade-in">
        <div className="container">
          <h2 className="text-center">
            {homeContent.sectionTitles?.services?.[currentLang] || (currentLang === 'pt' ? 'Serviços em Destaque' : 'Featured Services')}
          </h2>
          
          {services.length === 0 ? (
            <div className="services-empty">
              <p>{currentLang === 'pt' ? 'Nenhum serviço disponível.' : 'No services available.'}</p>
            </div>
          ) : (
            <>
              <div className="services-grid">
                {services.map((service, index) => (
                  <div 
                    key={service._id} 
                    className="service-card slide-in-left"
                    style={{ transitionDelay: `${index * 0.15}s` }}
                  >
                    <div className="service-image">
                      {service.image ? (
                        <img src={service.image} alt={service.name[currentLang]} />
                      ) : (
                        <div className="service-icon-large">{service.icon}</div>
                      )}
                    </div>
                    <div className="service-content">
                      <h3>{service.name[currentLang]}</h3>
                      <p>{service.description[currentLang]}</p>
                      <div className="service-price">€{service.price}</div>
                      {service.bookingEnabled && (
                        <Link to="/booking" className="btn btn-primary">
                          {currentLang === 'pt' ? 'Reservar Agora' : 'Book Now'}
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-center mt-4 fade-in">
                <Link to="/services" className="btn btn-secondary">
                  {currentLang === 'pt' ? 'Ver Todos os Serviços' : 'View All Services'}
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Testimonials - DYNAMIC */}
      {homeContent.testimonials && homeContent.testimonials.length > 0 && (
        <section className="section testimonials fade-in">
          <div className="container">
            <h2 className="text-center">
              {homeContent.sectionTitles?.testimonials?.[currentLang] || (currentLang === 'pt' ? 'O Que Dizem os Clientes' : 'What Clients Say')}
            </h2>
            <div className="testimonials-grid">
              {homeContent.testimonials
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((testimonial, index) => (
                  <div 
                    key={index} 
                    className="testimonial-card slide-in-right"
                    style={{ transitionDelay: `${index * 0.1}s` }}
                  >
                    <div className="testimonial-quote">"</div>
                    <p className="testimonial-text">{testimonial.text[currentLang]}</p>
                    <div className="testimonial-author">
                      <div className="testimonial-avatar">{testimonial.initial}</div>
                      <div className="testimonial-info">
                        <h4>{testimonial.name}</h4>
                        <p>{currentLang === 'pt' ? 'Cliente' : 'Client'}</p>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section - DYNAMIC */}
      <section className="cta fade-in">
        <div className="container">
          <h2>{homeContent.cta.title[currentLang]}</h2>
          <p>{homeContent.cta.description[currentLang]}</p>
          <Link to="/booking" className="btn">
            {homeContent.hero.ctaText[currentLang]}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
